import {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Tooltip,
  Divider,
  Slider,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import PencilCursor from "@/assets/icons/pencil.png";
import { useTranslation } from "react-i18next";

import {
  AnnotationEditorRef,
  AnnotationEditorProps,
  Tool,
  BlurMode,
  TextInputState,
  CropState,
  TextInputProps,
  TextFormatState,
} from "./types";
import {
  COLORS,
  FILL_COLORS,
  HIGHLIGHT_DEFAULT_COLOR,
  DEFAULT_THICKNESS,
  DEFAULT_BLUR,
  DEFAULT_FONT_SIZE,
} from "./constants";
import {
  StyledDialog,
  MainContainer,
  HeaderToolbar,
  LeftToolbar,
  RightToolbar,
  MainContent,
  DraggableToolPanel,
  DragHandle,
  CanvasContainer,
  FooterToolbar,
  LoadingOverlay,
  ToolButton,
  ColorPickerContainer,
  ColorButton,
} from "./styles";
import { useHistory } from "./hooks/useHistory";
import {
  drawArrow,
  applyBlurToRegion,
  applyTilesBlurToRegion,
  getCanvasCoordinates,
} from "./utils/canvasHelpers";
import { extractTextFromCanvas } from "./utils/ocrHelper";

import AnnotationToolbar from "./components/Toolbar";
import CropOverlay from "./components/CropOverlay";
import OcrResultModal from "./components/OcrResultModal";

// Icons for dialog tool panel
import SelectAllIcon from "@mui/icons-material/TouchAppOutlined";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CreateIcon from "@mui/icons-material/DrawOutlined";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { BlurOn } from "@mui/icons-material";
import HighlightIcon from "@mui/icons-material/BorderColor";
import CropIcon from "@mui/icons-material/Crop";

const TextInput = styled("input")<TextInputProps>(({ position }) => ({
  position: "absolute",
  left: position.x + "px",
  top: position.y + "px",
  border: "none",
  background: "transparent",
  outline: "none",
  padding: "2px 4px",
  margin: 0,
  zIndex: 3,
  pointerEvents: "auto",
  minWidth: "100px",
}));

type ShapeType = "rectangle" | "circle";

interface BaseShape {
  id: string;
  type: ShapeType;
  strokeColor: string;
  strokeWidth: number;
  fillColor: string | null;
  fillOpacity: number;
}

interface RectangleShape extends BaseShape {
  type: "rectangle";
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CircleShape extends BaseShape {
  type: "circle";
  cx: number;
  cy: number;
  radius: number;
}

type Shape = RectangleShape | CircleShape;

const AnnotationEditor = forwardRef<AnnotationEditorRef, AnnotationEditorProps>(
  (
    {
      open,
      onClose,
      imageUrl,
      onSave,
      embedded = false,
      toolbarPosition = "bottom",
      onInsertText,
    },
    ref
  ) => {
    // Canvas refs
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const tempCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const shapeCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const contextRef = useRef<CanvasRenderingContext2D | null>(null);
    const toolPanelRef = useRef<HTMLDivElement | null>(null);
    const canvasContainerRef = useRef<HTMLDivElement>(null);
    const pendingCropDataRef = useRef<string | null>(null);

    // Tool state
    const [activeTool, setActiveTool] = useState<Tool>("pencil");
    const [currentColor, setCurrentColor] = useState(COLORS[0]);
    const [thickness, setThickness] = useState(DEFAULT_THICKNESS);
    const [blurLevel, setBlurLevel] = useState(DEFAULT_BLUR);
    const [blurMode, setBlurMode] = useState<BlurMode>("normal");
    const [shapeFillColor, setShapeFillColor] = useState<string | null>(null);
    const [shapeFillOpacity, setShapeFillOpacity] = useState(0.3);

    const [shapes, setShapes] = useState<Shape[]>([]);
    const shapesRef = useRef<Shape[]>([]);
    const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);
    const [hoverShapeId, setHoverShapeId] = useState<string | null>(null);
    const [isDraggingShape, setIsDraggingShape] = useState(false);
    const [shapeDragOffset, setShapeDragOffset] = useState({ x: 0, y: 0 });

    // Text format state
    const [textFormat, setTextFormat] = useState<TextFormatState>({
      fontFamily: "Arial",
      fontSize: DEFAULT_FONT_SIZE,
      bold: false,
      italic: false,
      underline: false,
      textColor: "#FF0000",
      backgroundColor: null,
    });

    const handleTextFormatChange = (format: Partial<TextFormatState>) => {
      setTextFormat((prev) => ({ ...prev, ...format }));
    };

    const setShapesState = useCallback(
      (updater: Shape[] | ((prev: Shape[]) => Shape[])) => {
        setShapes((prev) => {
          const next = typeof updater === "function" ? (updater as (p: Shape[]) => Shape[])(prev) : updater;
          shapesRef.current = next;
          return next;
        });
      },
      []
    );

    // Drawing state
    const [isDrawing, setIsDrawing] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

    // Dialog draggable panel
    const [toolPanelPosition, setToolPanelPosition] = useState({ x: 20, y: 100 });
    const [isDraggingPanel, setIsDraggingPanel] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    // Text input
    const [textInput, setTextInput] = useState<TextInputState>({
      isActive: false,
      x: 0,
      y: 0,
      value: "",
    });

    // Crop state
    const [cropState, setCropState] = useState<CropState>({
      isActive: false,
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      aspectRatio: "free",
      isDragging: false,
      isResizing: false,
      resizeHandle: null,
      dragStartX: 0,
      dragStartY: 0,
    });

    // OCR state
    const [isOcrProcessing, setIsOcrProcessing] = useState(false);
    const [ocrText, setOcrText] = useState("");
    const [isOcrModalOpen, setIsOcrModalOpen] = useState(false);
    const [ocrSelectMode, setOcrSelectMode] = useState(false);

    const { t } = useTranslation(["bugherd"]);

    // History hook
    const {
      historyIndex,
      saveToHistory,
      undo: historyUndo,
      redo: historyRedo,
      resetHistory,
      clearToInitial,
      canUndo,
      canRedo,
    } = useHistory<Shape[]>();

    // ================ TOOL PANEL TOOLS (for dialog mode) ================
    const TOOLS: { id: Tool; icon: React.ReactNode; tooltip: string }[] = [
      { id: "select", icon: <SelectAllIcon />, tooltip: t("select") },
      { id: "rectangle", icon: <CropSquareIcon />, tooltip: t("rectangle") },
      { id: "circle", icon: <RadioButtonUncheckedIcon />, tooltip: t("circle") },
      { id: "arrow", icon: <ArrowForwardIcon />, tooltip: t("arrow") },
      { id: "pencil", icon: <CreateIcon />, tooltip: t("pencil") },
      { id: "blur", icon: <BlurOn />, tooltip: t("blur") },
      { id: "highlight", icon: <HighlightIcon />, tooltip: t("highlight") },
      { id: "crop", icon: <CropIcon />, tooltip: t("crop") },
      { id: "text", icon: <TextFieldsIcon />, tooltip: t("text") },
      { id: "clear", icon: <DeleteIcon />, tooltip: t("clearAll") },
    ];

    // ================ CANVAS INITIALIZATION ================
    const initializeCanvas = useCallback(async () => {
      if (!imageUrl || !open) return;
      setIsLoading(true);
      try {
        const loadImage = () =>
          new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = imageUrl;
          });

        await new Promise((resolve) => setTimeout(resolve, 100));
        const image = await loadImage();
        if (!canvasRef.current) return;
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        if (!context) return;

        let maxWidth: number;
        let maxHeight: number;

        if (embedded && canvasContainerRef.current) {
          const containerRect = canvasContainerRef.current.getBoundingClientRect();
          maxWidth = containerRect.width - 32;
          maxHeight = containerRect.height - 32;
        } else {
          const toolbarHeight = 128;
          maxWidth = window.innerWidth - 40;
          maxHeight = window.innerHeight - toolbarHeight - 40;
        }

        let newWidth = image.width;
        let newHeight = image.height;
        const aspectRatio = image.width / image.height;

        if (embedded) {
          const scaleX = maxWidth / newWidth;
          const scaleY = maxHeight / newHeight;
          const scale = Math.min(scaleX, scaleY, 1);
          newWidth = Math.floor(newWidth * scale);
          newHeight = Math.floor(newHeight * scale);
          if (newWidth > maxWidth) newWidth = Math.floor(maxWidth);
          if (newHeight > maxHeight) newHeight = Math.floor(maxHeight);
        } else {
          if (newWidth < maxWidth && newHeight < maxHeight) {
            const scaleX = maxWidth / newWidth;
            const scaleY = maxHeight / newHeight;
            const scale = Math.min(scaleX, scaleY, 2);
            newWidth = newWidth * scale;
            newHeight = newHeight * scale;
          } else {
            if (newWidth > maxWidth) {
              newWidth = maxWidth;
              newHeight = newWidth / aspectRatio;
            }
            if (newHeight > maxHeight) {
              newHeight = maxHeight;
              newWidth = newHeight * aspectRatio;
            }
          }
        }

        canvas.width = newWidth;
        canvas.height = newHeight;
        setCanvasSize({ width: newWidth, height: newHeight });

        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = "high";
        context.strokeStyle = currentColor;
        context.lineWidth = thickness;
        context.lineCap = "round";
        context.lineJoin = "round";
        contextRef.current = context;

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, newWidth, newHeight);

        if (tempCanvasRef.current) {
          const tempCanvas = tempCanvasRef.current;
          tempCanvas.width = newWidth;
          tempCanvas.height = newHeight;
          const tempContext = tempCanvas.getContext("2d");
          if (tempContext) {
            tempContext.imageSmoothingEnabled = true;
            tempContext.imageSmoothingQuality = "high";
            tempContext.strokeStyle = currentColor;
            tempContext.lineWidth = thickness;
            tempContext.lineCap = "round";
            tempContext.lineJoin = "round";
            tempContext.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
          }
        }

        if (shapeCanvasRef.current) {
          const shapeCanvas = shapeCanvasRef.current;
          shapeCanvas.width = newWidth;
          shapeCanvas.height = newHeight;
          const shapeContext = shapeCanvas.getContext("2d");
          if (shapeContext) {
            shapeContext.clearRect(0, 0, shapeCanvas.width, shapeCanvas.height);
          }
        }

        const initialState = canvas.toDataURL("image/png", 1.0);
        setShapesState([]);
        setSelectedShapeId(null);
        setHoverShapeId(null);
        resetHistory(initialState, []);
        setIsLoading(false);
      } catch (error) {
        console.error("Error initializing canvas:", error);
        setIsLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageUrl, open, embedded]);

    useEffect(() => {
      if (open && imageUrl) {
        const delay = embedded ? 300 : 100;
        const timer = setTimeout(() => initializeCanvas(), delay);
        return () => clearTimeout(timer);
      }
    }, [imageUrl, open, embedded, initializeCanvas]);

    useEffect(() => {
      if (open) initializeCanvas();
      return () => {
        resetHistory("", []);
        setIsLoading(true);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initializeCanvas]);

    useEffect(() => {
      if (!embedded || !open || !canvasContainerRef.current) return;
      let resizeTimer: ReturnType<typeof setTimeout>;
      const observer = new ResizeObserver(() => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => initializeCanvas(), 150);
      });
      observer.observe(canvasContainerRef.current);
      return () => {
        clearTimeout(resizeTimer);
        observer.disconnect();
      };
    }, [embedded, open, initializeCanvas]);


    // ================ CROP REDRAW (after React re-render clears canvas) ================
    useEffect(() => {
      const dataUrl = pendingCropDataRef.current;
      if (!dataUrl) return;
      pendingCropDataRef.current = null;

      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = currentColor;
        ctx.lineWidth = thickness;
        contextRef.current = ctx;

        if (tempCanvasRef.current) {
          const tempCtx = tempCanvasRef.current.getContext("2d");
          if (tempCtx) {
            tempCtx.clearRect(0, 0, tempCanvasRef.current.width, tempCanvasRef.current.height);
            tempCtx.lineCap = "round";
            tempCtx.lineJoin = "round";
          }
        }
      };
      img.src = dataUrl;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvasSize]);

    // ================ COLOR/STYLE SYNC ================
    useEffect(() => {
      if (contextRef.current) {
        contextRef.current.strokeStyle = currentColor;
        contextRef.current.fillStyle = currentColor;
        contextRef.current.lineWidth = thickness;
      }
      if (tempCanvasRef.current) {
        const tempContext = tempCanvasRef.current.getContext("2d");
        if (tempContext) {
          tempContext.strokeStyle = currentColor;
          tempContext.fillStyle = currentColor;
          tempContext.lineWidth = thickness;
        }
      }
    }, [currentColor, thickness]);

    useEffect(() => {
      if (!selectedShapeId) return;
      let didChange = false;
      setShapesState((prev) => {
        const next = prev.map((shape) => {
          if (shape.id !== selectedShapeId) return shape;
          const updated = {
            ...shape,
            strokeColor: currentColor,
            strokeWidth: thickness,
            fillColor: shapeFillColor,
            fillOpacity: shapeFillOpacity,
          };
          const isSame =
            shape.strokeColor === updated.strokeColor &&
            shape.strokeWidth === updated.strokeWidth &&
            shape.fillColor === updated.fillColor &&
            shape.fillOpacity === updated.fillOpacity;
          if (!isSame) didChange = true;
          return isSame ? shape : updated;
        });
        return didChange ? next : prev;
      });
      if (didChange) {
        saveToHistory(canvasRef.current, shapesRef.current);
      }
    }, [
      currentColor,
      thickness,
      shapeFillColor,
      shapeFillOpacity,
      selectedShapeId,
      saveToHistory,
      setShapesState,
    ]);

    // ================ TOOL SELECTION ================
    const handleToolSelect = (toolId: Tool) => {
      if (toolId === "clear") {
        clearToInitial(
          canvasRef.current,
          contextRef.current,
          tempCanvasRef.current,
          (restoredShapes, meta) => {
            setShapesState(restoredShapes ?? []);
            if (meta) {
              pendingCropDataRef.current = meta.imageData;
              setCanvasSize({ width: meta.width, height: meta.height });
            }
            setSelectedShapeId(null);
            setHoverShapeId(null);
            return Boolean(meta);
          },
          () => {
            saveToHistory(canvasRef.current, shapesRef.current);
          }
        );
        return;
      }

      // When switching to highlight, default to yellow if not already highlighted
      if (toolId === "highlight" && activeTool !== "highlight") {
        setCurrentColor(HIGHLIGHT_DEFAULT_COLOR);
      }

      // When switching to crop, initialize crop region
      if (toolId === "crop") {
        const w = canvasSize.width;
        const h = canvasSize.height;
        setCropState({
          isActive: true,
          x: w * 0.1,
          y: h * 0.1,
          width: w * 0.8,
          height: h * 0.8,
          aspectRatio: "free",
          isDragging: false,
          isResizing: false,
          resizeHandle: null,
          dragStartX: 0,
          dragStartY: 0,
        });
      } else {
        setCropState((prev) => ({ ...prev, isActive: false }));
      }

      setActiveTool(toolId);
      if (toolId !== "select") {
        setSelectedShapeId(null);
        setHoverShapeId(null);
        setIsDraggingShape(false);
      }
      if (contextRef.current) {
        contextRef.current.strokeStyle = currentColor;
        contextRef.current.lineWidth = thickness;
      }
    };

    // ================ COLOR CHANGE ================
    const handleColorChange = (color: string) => {
      setCurrentColor(color);
      if (contextRef.current) {
        contextRef.current.strokeStyle = color;
        contextRef.current.fillStyle = color;
      }
      if (tempCanvasRef.current) {
        const tempContext = tempCanvasRef.current.getContext("2d");
        if (tempContext) {
          tempContext.strokeStyle = color;
          tempContext.fillStyle = color;
        }
      }
    };

    // ================ TEXT INPUT ================
    const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (activeTool === "text") {
        if (textInput.isActive) finalizeText();
        const canvas = canvasRef.current;
        if (!canvas) return;
        const coords = getCanvasCoordinates(e, canvas);
        setTextInput({ isActive: true, x: coords.x, y: coords.y, value: "" });
      }
    };

    const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setTextInput((prev) => ({ ...prev, value: e.target.value }));
    };

    const handleTextInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") finalizeText();
      if (e.key === "Escape") setTextInput({ isActive: false, x: 0, y: 0, value: "" });
    };

    const buildFontString = (fmt: TextFormatState): string => {
      const parts: string[] = [];
      if (fmt.italic) parts.push("italic");
      if (fmt.bold) parts.push("bold");
      parts.push(`${fmt.fontSize}px`);
      parts.push(fmt.fontFamily);
      return parts.join(" ");
    };

    const finalizeText = () => {
      if (textInput.value && contextRef.current && canvasRef.current) {
        const ctx = contextRef.current;
        const fontStr = buildFontString(textFormat);
        ctx.font = fontStr;

        // Measure text for background rect and underline
        const metrics = ctx.measureText(textInput.value);
        const textWidth = metrics.width;
        const textHeight = textFormat.fontSize;

        // Draw background rectangle if set
        if (textFormat.backgroundColor) {
          ctx.save();
          ctx.fillStyle = textFormat.backgroundColor;
          // Position background behind text (y is baseline, so offset up by fontSize)
          ctx.fillRect(
            textInput.x - 2,
            textInput.y - textHeight + 2,
            textWidth + 4,
            textHeight + 4
          );
          ctx.restore();
        }

        // Draw text
        ctx.fillStyle = textFormat.textColor;
        ctx.font = fontStr;
        ctx.fillText(textInput.value, textInput.x, textInput.y);

        // Draw underline if set
        if (textFormat.underline) {
          ctx.save();
          ctx.strokeStyle = textFormat.textColor;
          ctx.lineWidth = Math.max(1, textFormat.fontSize / 15);
          ctx.beginPath();
          const underlineY = textInput.y + 3;
          ctx.moveTo(textInput.x, underlineY);
          ctx.lineTo(textInput.x + textWidth, underlineY);
          ctx.stroke();
          ctx.restore();
        }

        if (tempCanvasRef.current) {
          const tempContext = tempCanvasRef.current.getContext("2d");
          if (tempContext) {
            tempContext.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          }
        }
        saveToHistory(canvasRef.current, shapesRef.current);
      }
      setTextInput({ isActive: false, x: 0, y: 0, value: "" });
    };

    const DELETE_BUTTON_SIZE = 16;

    const normalizeRect = (x1: number, y1: number, x2: number, y2: number) => {
      const x = Math.min(x1, x2);
      const y = Math.min(y1, y2);
      const width = Math.abs(x2 - x1);
      const height = Math.abs(y2 - y1);
      return { x, y, width, height };
    };

    const getShapeBounds = (shape: Shape) => {
      if (shape.type === "rectangle") {
        return { x: shape.x, y: shape.y, width: shape.width, height: shape.height };
      }
      return {
        x: shape.cx - shape.radius,
        y: shape.cy - shape.radius,
        width: shape.radius * 2,
        height: shape.radius * 2,
      };
    };

    const getDeleteButtonBounds = (shape: Shape, padding = 0) => {
      const bounds = getShapeBounds(shape);
      return {
        x: bounds.x + bounds.width + padding - DELETE_BUTTON_SIZE / 2,
        y: bounds.y - padding - DELETE_BUTTON_SIZE / 2,
        size: DELETE_BUTTON_SIZE,
      };
    };

    const isPointInDeleteButton = (x: number, y: number, shape: Shape) => {
      const { x: bx, y: by, size } = getDeleteButtonBounds(shape, 6);
      return x >= bx && x <= bx + size && y >= by && y <= by + size;
    };

    const drawShape = (ctx: CanvasRenderingContext2D, shape: Shape) => {
      ctx.save();
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = shape.strokeColor;
      ctx.lineWidth = shape.strokeWidth;

      if (shape.type === "rectangle") {
        ctx.beginPath();
        ctx.rect(shape.x, shape.y, shape.width, shape.height);
        if (shape.fillColor) {
          ctx.save();
          ctx.globalAlpha = shape.fillOpacity;
          ctx.fillStyle = shape.fillColor;
          ctx.fill();
          ctx.restore();
        }
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.arc(shape.cx, shape.cy, shape.radius, 0, 2 * Math.PI);
        if (shape.fillColor) {
          ctx.save();
          ctx.globalAlpha = shape.fillOpacity;
          ctx.fillStyle = shape.fillColor;
          ctx.fill();
          ctx.restore();
        }
        ctx.stroke();
      }
      ctx.restore();
    };

    const drawSelection = (ctx: CanvasRenderingContext2D, shape: Shape) => {
      const bounds = getShapeBounds(shape);
      const padding = 6;
      const lineWidth = 2;
      const selectionBounds = {
        x: bounds.x - padding,
        y: bounds.y - padding,
        width: bounds.width + padding * 2,
        height: bounds.height + padding * 2,
      };

      ctx.save();
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = "#7C4DFF";
      ctx.lineWidth = lineWidth;
      ctx.strokeRect(
        selectionBounds.x,
        selectionBounds.y,
        selectionBounds.width,
        selectionBounds.height
      );
      ctx.restore();

      const { x, y, size } = getDeleteButtonBounds(shape, padding);
      ctx.save();
      ctx.beginPath();
      ctx.fillStyle = "#FFFFFF";
      ctx.strokeStyle = "#F44336";
      ctx.lineWidth = 1;
      ctx.arc(x + size / 2, y + size / 2, size / 2, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.strokeStyle = "#F44336";
      ctx.lineWidth = 1.5;
      ctx.moveTo(x + 4, y + 4);
      ctx.lineTo(x + size - 4, y + size - 4);
      ctx.moveTo(x + size - 4, y + 4);
      ctx.lineTo(x + 4, y + size - 4);
      ctx.stroke();
      ctx.restore();
    };

    const drawShapes = useCallback(
      (previewShape?: Shape) => {
        const canvas = shapeCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        shapes.forEach((shape) => drawShape(ctx, shape));
        if (previewShape) drawShape(ctx, previewShape);

        const selectedShape = shapes.find((shape) => shape.id === selectedShapeId);
        if (selectedShape) {
          drawSelection(ctx, selectedShape);
        }
      },
      [shapes, selectedShapeId]
    );

    const drawCompositeToTemp = (
      ctx: CanvasRenderingContext2D,
      canvas: HTMLCanvasElement
    ) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(canvas, 0, 0);
      if (shapeCanvasRef.current) {
        ctx.drawImage(shapeCanvasRef.current, 0, 0);
      }
    };

    const findShapeAtPoint = (x: number, y: number) => {
      const reversed = [...shapesRef.current].reverse();
      return reversed.find((shape) => {
        if (shape.type === "rectangle") {
          return (
            x >= shape.x &&
            x <= shape.x + shape.width &&
            y >= shape.y &&
            y <= shape.y + shape.height
          );
        }
        const dx = x - shape.cx;
        const dy = y - shape.cy;
        return Math.sqrt(dx * dx + dy * dy) <= shape.radius + shape.strokeWidth / 2;
      });
    };

    const selectShapeAtPoint = (x: number, y: number) => {
      const hitShape = findShapeAtPoint(x, y);
      if (!hitShape) {
        setSelectedShapeId(null);
        setHoverShapeId(null);
        setIsDraggingShape(false);
        return null;
      }

      setSelectedShapeId(hitShape.id);
      setHoverShapeId(hitShape.id);
      if (hitShape.type === "rectangle") {
        setShapeDragOffset({ x: x - hitShape.x, y: y - hitShape.y });
      } else {
        setShapeDragOffset({ x: x - hitShape.cx, y: y - hitShape.cy });
      }
      setCurrentColor(hitShape.strokeColor);
      setThickness(hitShape.strokeWidth);
      setShapeFillColor(hitShape.fillColor);
      setShapeFillOpacity(hitShape.fillOpacity);
      return hitShape;
    };

    useEffect(() => {
      const shapeCanvas = shapeCanvasRef.current;
      if (!shapeCanvas) return;
      shapeCanvas.width = canvasSize.width;
      shapeCanvas.height = canvasSize.height;
      drawShapes();
    }, [canvasSize, drawShapes]);

    useEffect(() => {
      drawShapes();
    }, [drawShapes]);

    // ================ DRAWING HANDLERS ================
    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (activeTool === "crop") return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const coords = getCanvasCoordinates(e, canvas);

      const selectedShape = shapesRef.current.find((shape) => shape.id === selectedShapeId);
      if (selectedShape && isPointInDeleteButton(coords.x, coords.y, selectedShape)) {
        const updatedShapes = shapesRef.current.filter((shape) => shape.id !== selectedShapeId);
        setShapesState(updatedShapes);
        setSelectedShapeId(null);
        setHoverShapeId(null);
        saveToHistory(canvasRef.current, updatedShapes);
        return;
      }

      const hitShape = selectShapeAtPoint(coords.x, coords.y);
      if (hitShape) {
        setIsDraggingShape(true);
        return;
      }
      if (activeTool === "select") return;

      if (!contextRef.current) return;
      setStartPos(coords);
      setIsDrawing(true);
      if (activeTool === "pencil") {
        contextRef.current.beginPath();
        contextRef.current.moveTo(coords.x, coords.y);
      }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const coords = getCanvasCoordinates(e, canvas);

      if (isDraggingShape && selectedShapeId) {
        setShapesState((prev) =>
          prev.map((shape) => {
            if (shape.id !== selectedShapeId) return shape;
            if (shape.type === "rectangle") {
              return {
                ...shape,
                x: coords.x - shapeDragOffset.x,
                y: coords.y - shapeDragOffset.y,
              };
            }
            return {
              ...shape,
              cx: coords.x - shapeDragOffset.x,
              cy: coords.y - shapeDragOffset.y,
            };
          })
        );
        return;
      }

      const hitShape = findShapeAtPoint(coords.x, coords.y);
      setHoverShapeId(hitShape ? hitShape.id : null);

      if (!isDrawing || !contextRef.current || !tempCanvasRef.current) return;

      if (activeTool === "rectangle" || activeTool === "circle") {
        const tempContext = tempCanvasRef.current.getContext("2d");
        if (tempContext) {
          tempContext.clearRect(0, 0, canvas.width, canvas.height);
        }
        if (activeTool === "rectangle") {
          const rect = normalizeRect(startPos.x, startPos.y, coords.x, coords.y);
          const previewShape: RectangleShape = {
            id: "preview",
            type: "rectangle",
            ...rect,
            strokeColor: currentColor,
            strokeWidth: thickness,
            fillColor: shapeFillColor,
            fillOpacity: shapeFillOpacity,
          };
          drawShapes(previewShape);
        } else {
          const radius = Math.sqrt(
            Math.pow(coords.x - startPos.x, 2) + Math.pow(coords.y - startPos.y, 2)
          );
          const previewShape: CircleShape = {
            id: "preview",
            type: "circle",
            cx: startPos.x,
            cy: startPos.y,
            radius,
            strokeColor: currentColor,
            strokeWidth: thickness,
            fillColor: shapeFillColor,
            fillOpacity: shapeFillOpacity,
          };
          drawShapes(previewShape);
        }
        return;
      }

      const tempContext = tempCanvasRef.current.getContext("2d");
      if (!tempContext) return;

      if (activeTool !== "text") {
        if (activeTool === "pencil") {
          tempContext.clearRect(0, 0, canvas.width, canvas.height);
          contextRef.current.lineWidth = thickness;
          contextRef.current.lineTo(coords.x, coords.y);
          contextRef.current.stroke();
          return;
        }

        drawCompositeToTemp(tempContext, canvas);
        tempContext.strokeStyle = currentColor;
        tempContext.lineWidth = thickness;

        switch (activeTool) {
          case "arrow":
            drawArrow(tempContext, startPos.x, startPos.y, coords.x, coords.y);
            break;

          case "highlight": {
            const w = coords.x - startPos.x;
            const h = coords.y - startPos.y;
            tempContext.save();
            tempContext.globalAlpha = 0.3;
            tempContext.fillStyle = currentColor;
            tempContext.fillRect(startPos.x, startPos.y, w, h);
            tempContext.restore();
            break;
          }

          case "blur": {
            const w = coords.x - startPos.x;
            const h = coords.y - startPos.y;
            tempContext.save();
            tempContext.setLineDash([5, 5]);
            tempContext.strokeStyle = "#7C4DFF";
            tempContext.lineWidth = 2;
            tempContext.strokeRect(startPos.x, startPos.y, w, h);
            tempContext.fillStyle = "rgba(124, 77, 255, 0.1)";
            tempContext.fillRect(startPos.x, startPos.y, w, h);
            tempContext.restore();
            break;
          }

          case "ocr": {
            // OCR region selection drawing
            if (ocrSelectMode) {
              const w = coords.x - startPos.x;
              const h = coords.y - startPos.y;
              tempContext.save();
              tempContext.setLineDash([5, 5]);
              tempContext.strokeStyle = "#2196F3";
              tempContext.lineWidth = 2;
              tempContext.strokeRect(startPos.x, startPos.y, w, h);
              tempContext.fillStyle = "rgba(33, 150, 243, 0.1)";
              tempContext.fillRect(startPos.x, startPos.y, w, h);
              tempContext.restore();
            }
            break;
          }
        }
      }
    };

    const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (isDraggingShape) {
        setIsDraggingShape(false);
        saveToHistory(canvasRef.current, shapesRef.current);
        return;
      }

      if (!isDrawing) return;
      setIsDrawing(false);

      const canvas = canvasRef.current;
      const ctx = contextRef.current;
      if (!canvas || !ctx || !tempCanvasRef.current) return;

      const coords = getCanvasCoordinates(e, canvas);

      if (activeTool === "rectangle") {
        const rect = normalizeRect(startPos.x, startPos.y, coords.x, coords.y);
        if (rect.width > 0 && rect.height > 0) {
          const newShape: RectangleShape = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            type: "rectangle",
            ...rect,
            strokeColor: currentColor,
            strokeWidth: thickness,
            fillColor: shapeFillColor,
            fillOpacity: shapeFillOpacity,
          };
          const updatedShapes = [...shapesRef.current, newShape];
          setShapesState(updatedShapes);
          saveToHistory(canvasRef.current, updatedShapes);
        }
        drawShapes();
        return;
      }

      if (activeTool === "circle") {
        const radius = Math.sqrt(
          Math.pow(coords.x - startPos.x, 2) + Math.pow(coords.y - startPos.y, 2)
        );
        if (radius > 0) {
          const newShape: CircleShape = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            type: "circle",
            cx: startPos.x,
            cy: startPos.y,
            radius,
            strokeColor: currentColor,
            strokeWidth: thickness,
            fillColor: shapeFillColor,
            fillOpacity: shapeFillOpacity,
          };
          const updatedShapes = [...shapesRef.current, newShape];
          setShapesState(updatedShapes);
          saveToHistory(canvasRef.current, updatedShapes);
        }
        drawShapes();
        return;
      }

      if (activeTool === "arrow") {
        ctx.drawImage(tempCanvasRef.current, 0, 0);
        const tempContext = tempCanvasRef.current.getContext("2d");
        if (tempContext) {
          tempContext.clearRect(0, 0, canvas.width, canvas.height);
        }
      }

      if (activeTool === "highlight") {
        ctx.save();
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = currentColor;
        ctx.fillRect(startPos.x, startPos.y, coords.x - startPos.x, coords.y - startPos.y);
        ctx.restore();

        // Sync temp canvas
        const tempContext = tempCanvasRef.current.getContext("2d");
        if (tempContext) {
          tempContext.clearRect(0, 0, canvas.width, canvas.height);
        }
      }

      if (activeTool === "blur") {
        const w = coords.x - startPos.x;
        const h = coords.y - startPos.y;
        if (blurMode === "tiles") {
          applyTilesBlurToRegion(canvas, ctx, startPos.x, startPos.y, w, h, blurLevel);
        } else {
          applyBlurToRegion(canvas, ctx, startPos.x, startPos.y, w, h, blurLevel);
        }
        const tempContext = tempCanvasRef.current.getContext("2d");
        if (tempContext) {
          tempContext.clearRect(0, 0, canvas.width, canvas.height);
        }
      }

      // OCR region selection
      if (activeTool === "ocr" && ocrSelectMode) {
        const w = coords.x - startPos.x;
        const h = coords.y - startPos.y;
        handleOcrRegion(startPos.x, startPos.y, Math.abs(w), Math.abs(h));
        setOcrSelectMode(false);
        setActiveTool("select");
        // Reset temp canvas
        const tempContext = tempCanvasRef.current.getContext("2d");
        if (tempContext) {
          tempContext.clearRect(0, 0, canvas.width, canvas.height);
        }
        return; // Don't save to history for OCR selection
      }

      saveToHistory(canvas, shapesRef.current);
    };

    // ================ UNDO/REDO ================
    const undo = () =>
      historyUndo(canvasRef.current, contextRef.current, tempCanvasRef.current, (restoredShapes, meta) => {
        setShapesState(restoredShapes ?? []);
        if (meta) {
          pendingCropDataRef.current = meta.imageData;
          setCanvasSize({ width: meta.width, height: meta.height });
        }
        setSelectedShapeId(null);
        setHoverShapeId(null);
        return Boolean(meta);
      });
    const redo = () =>
      historyRedo(canvasRef.current, contextRef.current, tempCanvasRef.current, (restoredShapes, meta) => {
        setShapesState(restoredShapes ?? []);
        if (meta) {
          pendingCropDataRef.current = meta.imageData;
          setCanvasSize({ width: meta.width, height: meta.height });
        }
        setSelectedShapeId(null);
        setHoverShapeId(null);
        return Boolean(meta);
      });

    // ================ CROP ================
    const getCanvasRect = (): DOMRect | null => {
      return canvasRef.current?.getBoundingClientRect() ?? null;
    };

    const handleApplyCrop = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Round to integers — canvas dimensions must be whole pixels
      const sx = Math.round(cropState.x);
      const sy = Math.round(cropState.y);
      const w = Math.round(cropState.width);
      const h = Math.round(cropState.height);
      if (w < 1 || h < 1) return;

      // 1. Capture cropped region into an offscreen canvas
      const cropped = document.createElement("canvas");
      cropped.width = w;
      cropped.height = h;
      const croppedCtx = cropped.getContext("2d");
      if (!croppedCtx) return;
      croppedCtx.drawImage(canvas, sx, sy, w, h, 0, 0, w, h);

      // 2. Save cropped image as data URL — React will clear the canvas
      //    when it re-renders with new width/height, so we redraw in a useEffect
      const croppedDataUrl = cropped.toDataURL("image/png", 1.0);
      pendingCropDataRef.current = croppedDataUrl;

      const cropBounds = { x: sx, y: sy, width: w, height: h };
      const updatedShapes = shapesRef.current
        .map((shape) => {
          const bounds = getShapeBounds(shape);
          const intersects =
            bounds.x + bounds.width >= cropBounds.x &&
            bounds.x <= cropBounds.x + cropBounds.width &&
            bounds.y + bounds.height >= cropBounds.y &&
            bounds.y <= cropBounds.y + cropBounds.height;
          if (!intersects) return null;
          if (shape.type === "rectangle") {
            return {
              ...shape,
              x: shape.x - cropBounds.x,
              y: shape.y - cropBounds.y,
            };
          }
          return {
            ...shape,
            cx: shape.cx - cropBounds.x,
            cy: shape.cy - cropBounds.y,
          };
        })
        .filter((shape): shape is Shape => Boolean(shape));

      // 3. Trigger state updates (React re-render will set canvas dimensions)
      setCanvasSize({ width: w, height: h });
      setCropState((prev) => ({ ...prev, isActive: false }));
      setActiveTool("select");
      setShapesState(updatedShapes);
      setSelectedShapeId(null);
      setHoverShapeId(null);
      saveToHistory(cropped, updatedShapes);
    };

    const handleCancelCrop = () => {
      setCropState((prev) => ({ ...prev, isActive: false }));
      setActiveTool("select");
    };

    // ================ OCR ================
    const handleOcrFullScreenshot = async () => {
      if (!canvasRef.current) return;
      setIsOcrProcessing(true);
      try {
        const result = await extractTextFromCanvas(canvasRef.current);
        setOcrText(result.text);
        setIsOcrModalOpen(true);
      } catch (err) {
        console.error("OCR error:", err);
        setOcrText("");
        setIsOcrModalOpen(true);
      }
      setIsOcrProcessing(false);
    };

    const handleOcrSelectRegion = () => {
      setOcrSelectMode(true);
      setActiveTool("ocr");
    };

    const handleOcrRegion = async (x: number, y: number, w: number, h: number) => {
      if (!canvasRef.current) return;
      setIsOcrProcessing(true);
      try {
        const result = await extractTextFromCanvas(canvasRef.current, {
          x: Math.min(x, x + w),
          y: Math.min(y, y + h),
          width: w,
          height: h,
        });
        setOcrText(result.text);
        setIsOcrModalOpen(true);
      } catch (err) {
        console.error("OCR error:", err);
        setOcrText("");
        setIsOcrModalOpen(true);
      }
      setIsOcrProcessing(false);
    };

    // ================ SAVE ================
    const getMergedImageData = () => {
      const baseCanvas = canvasRef.current;
      if (!baseCanvas) return null;
      const merged = document.createElement("canvas");
      merged.width = baseCanvas.width;
      merged.height = baseCanvas.height;
      const mergedCtx = merged.getContext("2d");
      if (!mergedCtx) return baseCanvas.toDataURL("image/png", 1.0);
      mergedCtx.drawImage(baseCanvas, 0, 0);
      if (shapeCanvasRef.current) {
        mergedCtx.drawImage(shapeCanvasRef.current, 0, 0);
      }
      return merged.toDataURL("image/png", 1.0);
    };

    const handleSave = () => {
      const imageData = getMergedImageData();
      if (imageData) onSave(imageData);
    };

    useImperativeHandle(
      ref,
      () => ({
        saveAnnotation: () => {
          const imageData = getMergedImageData();
          if (imageData) {
            onSave(imageData);
            return imageData;
          }
          return null;
        },
        isDirty: () => historyIndex > 0,
      }),
      [onSave, historyIndex]
    );

    // ================ DRAGGABLE PANEL (dialog mode) ================
    const handlePanelMouseDown = (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest(".drag-handle")) {
        setIsDraggingPanel(true);
        if (toolPanelRef.current) {
          const rect = toolPanelRef.current.getBoundingClientRect();
          setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        }
        e.preventDefault();
      }
    };

    const handlePanelMouseMove = useCallback(
      (e: MouseEvent) => {
        if (isDraggingPanel) {
          const newX = e.clientX - dragOffset.x;
          const newY = e.clientY - dragOffset.y;
          const maxX = window.innerWidth - 80;
          const maxY = window.innerHeight - 200;
          setToolPanelPosition({
            x: Math.max(0, Math.min(newX, maxX)),
            y: Math.max(64, Math.min(newY, maxY)),
          });
        }
      },
      [isDraggingPanel, dragOffset]
    );

    const handlePanelMouseUp = useCallback(() => setIsDraggingPanel(false), []);

    useEffect(() => {
      if (isDraggingPanel) {
        document.addEventListener("mousemove", handlePanelMouseMove);
        document.addEventListener("mouseup", handlePanelMouseUp);
        return () => {
          document.removeEventListener("mousemove", handlePanelMouseMove);
          document.removeEventListener("mouseup", handlePanelMouseUp);
        };
      }
    }, [isDraggingPanel, handlePanelMouseMove, handlePanelMouseUp]);

    // ================ CURSOR ================
    const getCursor = () => {
      if (activeTool === "text") return "text";
      if (activeTool === "select") return hoverShapeId ? "move" : "default";
      if (activeTool === "pencil") return `url(${PencilCursor}) 0 24, auto`;
      if (activeTool === "crop") return "default";
      return "crosshair";
    };

    // ================ POPUP DIRECTION ================
    const popupAbove = toolbarPosition === "bottom";
    const isShapeTool = activeTool === "rectangle" || activeTool === "circle";
    const isShapeSelectionActive = Boolean(selectedShapeId);

    // ================ CANVAS SECTION ================
    const canvasSection = (
      <Box
        ref={canvasContainerRef}
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          position: "relative",
          backgroundColor: "#f5f5f5",
          padding: 2,
          minHeight: 0,
        }}
      >
        {isLoading && (
          <LoadingOverlay>
            <CircularProgress />
          </LoadingOverlay>
        )}
        {isOcrProcessing && (
          <LoadingOverlay>
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t("ocrProcessing")}
              </Typography>
            </Box>
          </LoadingOverlay>
        )}

        {textInput.isActive && (
          <TextInput
            autoFocus
            value={textInput.value}
            onChange={handleTextInputChange}
            onKeyDown={handleTextInputKeyDown}
            onBlur={finalizeText}
            onClick={(e) => e.stopPropagation()}
            position={{ x: textInput.x, y: textInput.y }}
            style={{
              color: textFormat.textColor,
              font: buildFontString(textFormat),
              backgroundColor: textFormat.backgroundColor || "transparent",
              textDecoration: textFormat.underline ? "underline" : "none",
            }}
          />
        )}

        <Box sx={{ position: "relative", maxWidth: "100%", maxHeight: "100%" }}>
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onClick={handleCanvasClick}
            style={{
              cursor: getCursor(),
              display: "block",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              maxWidth: "100%",
              maxHeight: "100%",
            }}
          />
          <canvas
            ref={shapeCanvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
              maxWidth: "100%",
              maxHeight: "100%",
            }}
          />
          <canvas
            ref={tempCanvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
              maxWidth: "100%",
              maxHeight: "100%",
            }}
          />

          {/* Crop Overlay */}
          {cropState.isActive && (
            <CropOverlay
              canvasWidth={canvasSize.width}
              canvasHeight={canvasSize.height}
              cropState={cropState}
              onCropStateChange={setCropState}
              onApply={handleApplyCrop}
              onCancel={handleCancelCrop}
              canvasRect={getCanvasRect()}
            />
          )}
        </Box>
      </Box>
    );

    // ================ TOOLBAR SECTION (embedded mode) ================
    const toolbarSection = (
      <AnnotationToolbar
        activeTool={activeTool}
        onToolSelect={handleToolSelect}
        currentColor={currentColor}
        onColorChange={handleColorChange}
        shapeFillColor={shapeFillColor}
        onShapeFillColorChange={setShapeFillColor}
        shapeFillOpacity={shapeFillOpacity}
        onShapeFillOpacityChange={setShapeFillOpacity}
        showShapeControls={isShapeTool || isShapeSelectionActive}
        thickness={thickness}
        onThicknessChange={setThickness}
        blurLevel={blurLevel}
        onBlurLevelChange={setBlurLevel}
        blurMode={blurMode}
        onBlurModeChange={setBlurMode}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undo}
        onRedo={redo}
        popupAbove={popupAbove}
        onOcrFullScreenshot={handleOcrFullScreenshot}
        onOcrSelectRegion={handleOcrSelectRegion}
        isOcrProcessing={isOcrProcessing}
        textFormat={textFormat}
        onTextFormatChange={handleTextFormatChange}
      />
    );

    // ================ EMBEDDED LAYOUT ================
    const embeddedContent = (
      <MainContainer>
        {toolbarPosition === "top" && toolbarSection}
        {canvasSection}
        {toolbarPosition === "bottom" && toolbarSection}
        <OcrResultModal
          open={isOcrModalOpen}
          onClose={() => setIsOcrModalOpen(false)}
          text={ocrText}
          onInsertText={onInsertText}
        />
      </MainContainer>
    );

    // ================ DIALOG LAYOUT ================
    const dialogContent = (
      <MainContainer>
        <HeaderToolbar>
          <LeftToolbar>
            <Typography variant="h6">{t("Edit Screenshot")}</Typography>
          </LeftToolbar>
          <RightToolbar>
            <IconButton onClick={undo} disabled={!canUndo}>
              <UndoIcon />
            </IconButton>
            <IconButton onClick={redo} disabled={!canRedo}>
              <RedoIcon />
            </IconButton>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </RightToolbar>
        </HeaderToolbar>

        <MainContent>
          <DraggableToolPanel
            ref={toolPanelRef}
            x={toolPanelPosition.x}
            y={toolPanelPosition.y}
            onMouseDown={handlePanelMouseDown}
            elevation={8}
          >
            <DragHandle className="drag-handle">
              <DragIndicatorIcon fontSize="small" />
            </DragHandle>

            {TOOLS.map((tool) => (
              <Tooltip key={tool.id} title={tool.tooltip} placement="right">
                <ToolButton
                  size="small"
                  active={activeTool === tool.id}
                  onClick={() => handleToolSelect(tool.id)}
                >
                  {tool.icon}
                </ToolButton>
              </Tooltip>
            ))}

            <Divider sx={{ width: "100%", my: 1 }} />

            <ColorPickerContainer onClick={(e) => e.stopPropagation()}>
              {COLORS.map((color) => (
                <ColorButton
                  key={color}
                  color={color}
                  selected={currentColor === color}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleColorChange(color);
                  }}
                />
              ))}
            </ColorPickerContainer>

            {/* Shape Fill Color + Opacity (rectangle/circle only) */}
            {(isShapeTool || isShapeSelectionActive) && (
              <>
                <Divider sx={{ width: "100%", my: 1 }} />
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10 }}>
                  {t("fill", "Fill")}
                </Typography>
                <ColorPickerContainer onClick={(e) => e.stopPropagation()}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      cursor: "pointer",
                      border:
                        shapeFillColor === null
                          ? "2px solid #1976d2"
                          : "1px solid #ccc",
                      position: "relative",
                      overflow: "hidden",
                      backgroundColor: "#fff",
                      "&:hover": { transform: "scale(1.1)" },
                    }}
                    onClick={() => setShapeFillColor(null)}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "-10%",
                        width: "120%",
                        height: "2px",
                        backgroundColor: "#F44336",
                        transform: "rotate(-45deg)",
                        transformOrigin: "center",
                      }}
                    />
                  </Box>
                  {FILL_COLORS.filter((color) => color !== null).map((color) => (
                    <ColorButton
                      key={color as string}
                      color={color as string}
                      selected={shapeFillColor === color}
                      onClick={() => setShapeFillColor(color as string)}
                    />
                  ))}
                </ColorPickerContainer>

                <Divider sx={{ width: "100%", my: 1 }} />
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10 }}>
                    {t("opacity", "Opacity")}: {Math.round(shapeFillOpacity * 100)}%
                  </Typography>
                  <Slider
                    value={Math.round(shapeFillOpacity * 100)}
                    onChange={(_, value) => setShapeFillOpacity((value as number) / 100)}
                    min={0}
                    max={100}
                    size="small"
                    sx={{
                      width: "90%",
                      color: "#7C4DFF",
                      "& .MuiSlider-thumb": { width: 12, height: 12 },
                    }}
                  />
                </Box>
              </>
            )}

            {/* Thickness Slider */}
            <Divider sx={{ width: "100%", my: 1 }} />
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10 }}>
                {t("thickness")}: {thickness}
              </Typography>
              <Slider
                value={thickness}
                onChange={(_, value) => setThickness(value as number)}
                min={1}
                max={20}
                size="small"
                sx={{
                  width: "90%",
                  color: "#7C4DFF",
                  "& .MuiSlider-thumb": { width: 12, height: 12 },
                }}
              />
            </Box>

            {/* Blur Level Slider - shown when blur tool is active */}
            {activeTool === "blur" && (
              <>
                <Divider sx={{ width: "100%", my: 1 }} />
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10 }}>
                    {t("blur")}: {blurLevel}
                  </Typography>
                  <Slider
                    value={blurLevel}
                    onChange={(_, value) => setBlurLevel(value as number)}
                    min={2}
                    max={20}
                    size="small"
                    sx={{
                      width: "90%",
                      color: "#7C4DFF",
                      "& .MuiSlider-thumb": { width: 12, height: 12 },
                      "& .MuiSlider-track": { backgroundColor: "#7C4DFF" },
                      "& .MuiSlider-rail": { backgroundColor: "rgba(124, 77, 255, 0.3)" },
                    }}
                  />
                </Box>
              </>
            )}
          </DraggableToolPanel>

          <CanvasContainer>
            {isLoading && (
              <LoadingOverlay>
                <CircularProgress />
              </LoadingOverlay>
            )}
            {isOcrProcessing && (
              <LoadingOverlay>
                <Box sx={{ textAlign: "center" }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t("ocrProcessing")}
                  </Typography>
                </Box>
              </LoadingOverlay>
            )}

            {textInput.isActive && (
              <TextInput
                autoFocus
                value={textInput.value}
                onChange={handleTextInputChange}
                onKeyDown={handleTextInputKeyDown}
                onBlur={finalizeText}
                onClick={(e) => e.stopPropagation()}
                position={{ x: textInput.x, y: textInput.y }}
                style={{
                  color: textFormat.textColor,
                  font: buildFontString(textFormat),
                  backgroundColor: textFormat.backgroundColor || "transparent",
                  textDecoration: textFormat.underline ? "underline" : "none",
                }}
              />
            )}

            <Box sx={{ position: "relative" }}>
              <canvas
                ref={canvasRef}
                width={canvasSize.width}
                height={canvasSize.height}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onClick={handleCanvasClick}
                style={{
                  cursor: getCursor(),
                  display: "block",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  maxWidth: "100%",
                  maxHeight: "100%",
                }}
              />
              <canvas
                ref={shapeCanvasRef}
                width={canvasSize.width}
                height={canvasSize.height}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  pointerEvents: "none",
                }}
              />
              <canvas
                ref={tempCanvasRef}
                width={canvasSize.width}
                height={canvasSize.height}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  pointerEvents: "none",
                }}
              />

              {cropState.isActive && (
                <CropOverlay
                  canvasWidth={canvasSize.width}
                  canvasHeight={canvasSize.height}
                  cropState={cropState}
                  onCropStateChange={setCropState}
                  onApply={handleApplyCrop}
                  onCancel={handleCancelCrop}
                  canvasRect={getCanvasRect()}
                />
              )}
            </Box>
          </CanvasContainer>
        </MainContent>

        <FooterToolbar>
          <Button variant="contained" onClick={handleSave} size="small">
            {t("Save")}
          </Button>
        </FooterToolbar>

        <OcrResultModal
          open={isOcrModalOpen}
          onClose={() => setIsOcrModalOpen(false)}
          text={ocrText}
          onInsertText={onInsertText}
        />
      </MainContainer>
    );

    if (embedded) {
      return open ? embeddedContent : null;
    }

    return (
      <StyledDialog open={open} onClose={onClose} maxWidth={false}>
        {dialogContent}
      </StyledDialog>
    );
  }
);

export default AnnotationEditor;
export type { AnnotationEditorRef } from "./types";
